import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAdminApi } from '../hooks/useAdminApi';
import { 
  Users, 
  MapPin, 
  Phone, 
  Clock,
  CheckCircle,
  XCircle,
  Search,
  RefreshCw,
  ArrowLeft,
  ChevronRight,
  UserPlus,
  Mail,
  X,
  Send,
  MoreVertical,
  PauseCircle,
  UserMinus,
  UserCheck,
  AlertTriangle,
  Archive,
  RotateCcw,
  Trash2,
  KeyRound
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { toast } from 'sonner';

const SUSPEND_REASONS = [
  "Arrêt maladie",
  "Congé parental",
  "Congé sans solde",
  "Suspension temporaire",
  "Autre"
];

const TERMINATE_REASONS = [
  "Mise à pied",
  "Démission",
  "Fin de contrat",
  "Retraite",
  "Autre"
];

const DriversPage = () => {
  const { get, post, patch, del } = useAdminApi();
  const [drivers, setDrivers] = useState([]);
  const [archivedDrivers, setArchivedDrivers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [activeTab, setActiveTab] = useState('active'); // 'active' | 'archived'
  const [showInviteModal, setShowInviteModal] = useState(false);
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteLoading, setInviteLoading] = useState(false);
  const [connectedDrivers, setConnectedDrivers] = useState({ active: [], suspended: [], pending: [] });
  const [pendingInvitations, setPendingInvitations] = useState([]);
  const [invitationFallback, setInvitationFallback] = useState(null);
  
  // Status action modal
  const [statusModal, setStatusModal] = useState(null); // { driver, action: 'suspend'|'terminate' }
  const [statusReason, setStatusReason] = useState('');
  const [statusDetail, setStatusDetail] = useState('');
  const [statusLoading, setStatusLoading] = useState(false);
  
  // Action menu
  const [openMenuId, setOpenMenuId] = useState(null);
  const [resetPasswordResult, setResetPasswordResult] = useState(null);

  const fetchDrivers = async () => {
    setLoading(true);
    const result = await get('/saas/dashboard/drivers');
    if (result.success) {
      setDrivers(result.data);
    }
    
    const connResult = await get('/driver/admin/connected-drivers');
    if (connResult.success) {
      setConnectedDrivers(connResult.data);
    }

    const invResult = await get('/driver/admin/invitations');
    if (invResult.success) {
      // Ne garder que les invitations non acceptees (pending ou expired)
      const pending = (invResult.data || []).filter(inv => inv.status !== 'accepted');
      setPendingInvitations(pending);
    }

    setLoading(false);
  };

  const fetchArchivedDrivers = async () => {
    const result = await get('/saas/drivers/archived');
    if (result.success) {
      setArchivedDrivers(result.data);
    }
  };

  const handleInvite = async (e) => {
    e.preventDefault();
    if (!inviteEmail) return;
    
    setInviteLoading(true);
    try {
      const result = await post('/driver/invite', { driver_email: inviteEmail });
      if (result.success) {
        toast.success(result.data.message || 'Invitation envoyée!');
        setShowInviteModal(false);
        setInviteEmail('');
        fetchDrivers();
      } else {
        toast.error(result.error || "Erreur lors de l'envoi");
      }
    } catch (error) {
      toast.error("Erreur lors de l'envoi de l'invitation");
    } finally {
      setInviteLoading(false);
    }
  };

  const handleStatusChange = async (driverId, newStatus, reason = '', detail = '') => {
    setStatusLoading(true);
    try {
      const result = await patch(`/saas/drivers/${driverId}/status`, {
        status: newStatus,
        reason,
        reason_detail: detail
      });
      if (result.success) {
        toast.success(result.data.message);
        setStatusModal(null);
        setStatusReason('');
        setStatusDetail('');
        fetchDrivers();
        fetchArchivedDrivers();
      } else {
        toast.error(result.error || 'Erreur');
      }
    } catch (error) {
      toast.error('Erreur lors du changement de statut');
    } finally {
      setStatusLoading(false);
    }
  };

  const handleReinvite = async (driverId) => {
    try {
      const result = await post(`/saas/drivers/${driverId}/reinvite`, {});
      if (result.success) {
        toast.success(result.data.message);
        fetchDrivers();
        fetchArchivedDrivers();
      } else {
        toast.error(result.error || 'Erreur');
      }
    } catch (error) {
      toast.error('Erreur lors de la réactivation');
    }
  };

  const handleResendInvitation = async (invitationId, driverEmail) => {
    const confirmed = window.confirm(
      `Renvoyer l'invitation à ${driverEmail} ?\n\n` +
      `Un nouvel email sera envoyé avec un lien fraîchement généré (valide 7 jours).`
    );
    if (!confirmed) return;
    try {
      const result = await post(`/driver/admin/invitations/${invitationId}/resend`, {});
      if (result.success) {
        if (result.data.email_sent) {
          toast.success(result.data.message || 'Invitation renvoyée!');
        } else {
          toast.warning('Email non envoyé - utilisez le lien manuel');
          setInvitationFallback({
            url: result.data.invitation_url,
            email: driverEmail,
            emailError: result.data.email_error,
            isExistingUser: result.data.is_existing_user,
          });
        }
        fetchDrivers();
      } else {
        toast.error(result.error || 'Erreur lors du renvoi');
      }
    } catch (error) {
      toast.error("Erreur lors du renvoi de l'invitation");
    }
  };

  const handleForceCreateConnection = async (invitationId, driverEmail) => {
    const confirmed = window.confirm(
      `Créer manuellement la connexion pour ${driverEmail} ?\n\n` +
      `Le chauffeur sera ajouté immédiatement à votre flotte, sans qu'il ait besoin de cliquer sur le lien d'invitation.\n\n` +
      `\u26a0\ufe0f Le compte chauffeur doit déjà exister avec cet email.`
    );
    if (!confirmed) return;
    try {
      const result = await post(`/driver/admin/invitations/${invitationId}/force-create-connection`, {});
      if (result.success) {
        toast.success(result.data.message || 'Connexion créée!');
        fetchDrivers();
      } else {
        toast.error(result.error || 'Impossible de créer la connexion');
      }
    } catch (error) {
      toast.error('Erreur lors de la création de la connexion');
    }
  };

  // --- DELETE-INVITATION-BTN-V1 ---
  const handleDeleteInvitation = async (invitationId, driverEmail) => {
    const confirmed = window.confirm(
      `Supprimer définitivement l'invitation pour ${driverEmail} ?\n\n` +
      `Cette action est irréversible. Utilisez cette option pour nettoyer les invitations obsolètes ou envoyées à une mauvaise adresse.`
    );
    if (!confirmed) return;
    try {
      const result = await del(`/driver/admin/invitations/${invitationId}`);
      if (result.success) {
        toast.success('Invitation supprimée');
        fetchDrivers();
      } else {
        toast.error(result.error || 'Erreur lors de la suppression');
      }
    } catch (error) {
      toast.error('Erreur lors de la suppression');
    }
  };


  const handleResetPassword = async (driverId, driverName) => {
    const confirmed = window.confirm(`Reinitialiser le mot de passe de ${driverName} ?\n\nUn mot de passe temporaire sera genere. L'ancien ne fonctionnera plus.\nVous devrez le transmettre au chauffeur par SMS.`);
    if (!confirmed) return;
    try {
      const result = await post(`/driver/${driverId}/reset-password`, {});
      if (result.success) {
        setResetPasswordResult({ email: result.data.driver_email, name: result.data.driver_name, password: result.data.temp_password });
        toast.success('Mot de passe reinitialise');
      } else { toast.error(result.error || 'Erreur'); }
    } catch { toast.error('Erreur'); }
  };
  const handleCopyResetPassword = async () => {
    if (!resetPasswordResult?.password) return;
    try { await navigator.clipboard.writeText(resetPasswordResult.password); toast.success('Mot de passe copie'); }
    catch { toast.error('Copie manuelle requise'); }
  };
  const handleShareResetSMS = () => {
    if (!resetPasswordResult) return;
    const msg = `Bonjour ${resetPasswordResult.name}, votre mot de passe ProPilote a ete reinitialise. Email: ${resetPasswordResult.email} - Mot de passe temporaire: ${resetPasswordResult.password} (a taper tel quel). Changez-le apres connexion depuis votre profil.`;
    window.location.href = `sms:?&body=${encodeURIComponent(msg)}`;
  };


  useEffect(() => {
    fetchDrivers();
    fetchArchivedDrivers();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Close menu when clicking outside
  useEffect(() => {
    const handleClick = () => setOpenMenuId(null);
    if (openMenuId) document.addEventListener('click', handleClick);
    return () => document.removeEventListener('click', handleClick);
  }, [openMenuId]);

  const activeDrivers = drivers.filter(d => d.driver_status !== 'suspended');
  const suspendedDrivers = drivers.filter(d => d.driver_status === 'suspended');
  
  const filteredActive = activeDrivers.filter(driver => {
    const name = `${driver.first_name} ${driver.last_name}`.toLowerCase();
    return name.includes(searchTerm.toLowerCase());
  });

  const filteredSuspended = suspendedDrivers.filter(driver => {
    const name = `${driver.first_name} ${driver.last_name}`.toLowerCase();
    return name.includes(searchTerm.toLowerCase());
  });

  const filteredArchived = archivedDrivers.filter(driver => {
    const name = `${driver.first_name} ${driver.last_name}`.toLowerCase();
    return name.includes(searchTerm.toLowerCase());
  });

  const onlineCount = activeDrivers.filter(d => d.is_online).length;

  const DriverCard = ({ driver, showActions = true, isArchived = false }) => {
    const isSuspended = driver.driver_status === 'suspended';
    
    return (
      <div 
        className={`bg-slate-900 border rounded-xl p-4 transition-all ${
          isSuspended 
            ? 'border-amber-500/40 opacity-75' 
            : isArchived 
              ? 'border-red-500/30 opacity-70'
              : 'border-slate-800 hover:border-amber-500/50 hover:bg-slate-800/50'
        }`}
      >
        <div className="flex items-start justify-between mb-3">
          <Link 
            to={isArchived ? '#' : `/admin/drivers/${driver.id}`}
            className={`flex items-center gap-3 flex-1 ${isArchived ? 'pointer-events-none' : 'group cursor-pointer'}`}
          >
            <div className={`w-12 h-12 rounded-full flex items-center justify-center text-xl font-bold transition-colors ${
              isSuspended 
                ? 'bg-amber-500/20 text-amber-500'
                : isArchived
                  ? 'bg-red-500/20 text-red-400'
                  : 'bg-slate-800 text-amber-500 group-hover:bg-amber-500/20'
            }`}>
              {driver.first_name?.[0]}{driver.last_name?.[0]}
            </div>
            <div>
              <p className="text-white font-semibold group-hover:text-amber-500 transition-colors">
                {driver.first_name} {driver.last_name}
              </p>
              <p className="text-slate-400 text-sm">{driver.email}</p>
              {driver.missing_truck_today && (
                <p className="flex items-center gap-1 text-xs text-amber-400 mt-0.5" data-testid={`driver-missing-truck-${driver.id}`}>
                  <AlertTriangle size={12} /> Aucun véhicule choisi aujourd'hui
                </p>
              )}
            </div>
          </Link>
          
          <div className="flex items-center gap-2">
            {/* Status badge */}
            {isSuspended ? (
              <span className="flex items-center gap-1 px-2 py-1 rounded-full text-xs bg-amber-500/20 text-amber-400">
                <PauseCircle size={12} />
                Suspendu
              </span>
            ) : isArchived ? (
              <span className="flex items-center gap-1 px-2 py-1 rounded-full text-xs bg-red-500/20 text-red-400">
                <UserMinus size={12} />
                Retiré
              </span>
            ) : (
              <span className={`flex items-center gap-1 px-2 py-1 rounded-full text-xs ${
                driver.is_online 
                  ? 'bg-green-500/20 text-green-400' 
                  : 'bg-slate-800 text-slate-400'
              }`}>
                {driver.is_online ? <CheckCircle size={12} /> : <XCircle size={12} />}
                {driver.is_online ? 'En ligne' : 'Hors ligne'}
              </span>
            )}
            
            {/* Action menu */}
            {showActions && (
              <div className="relative">
                <button 
                  onClick={(e) => { e.stopPropagation(); setOpenMenuId(openMenuId === driver.id ? null : driver.id); }}
                  className="p-1 rounded hover:bg-slate-700 text-slate-400 hover:text-white transition-colors"
                  data-testid={`driver-menu-${driver.id}`}
                >
                  <MoreVertical size={18} />
                </button>
                
                {openMenuId === driver.id && (
                  <div className="absolute right-0 top-8 bg-slate-800 border border-slate-700 rounded-lg shadow-xl z-50 min-w-[200px] py-1">
                    {isSuspended ? (
                      <>
                        <button 
                          onClick={(e) => { e.stopPropagation(); handleStatusChange(driver.id, 'active'); setOpenMenuId(null); }}
                          className="w-full flex items-center gap-2 px-4 py-2.5 text-sm text-green-400 hover:bg-slate-700 transition-colors"
                          data-testid={`reactivate-${driver.id}`}
                        >
                          <UserCheck size={16} />
                          Réactiver
                        </button>
                        <button 
                          onClick={(e) => { e.stopPropagation(); setStatusModal({ driver, action: 'terminate' }); setOpenMenuId(null); }}
                          className="w-full flex items-center gap-2 px-4 py-2.5 text-sm text-red-400 hover:bg-slate-700 transition-colors"
                          data-testid={`terminate-${driver.id}`}
                        >
                          <UserMinus size={16} />
                          Retirer définitivement
                        </button>
                      </>
                    ) : isArchived ? (
                      <button 
                        onClick={(e) => { e.stopPropagation(); handleReinvite(driver.id); setOpenMenuId(null); }}
                        className="w-full flex items-center gap-2 px-4 py-2.5 text-sm text-green-400 hover:bg-slate-700 transition-colors"
                        data-testid={`reinvite-${driver.id}`}
                      >
                        <RotateCcw size={16} />
                        Réinviter
                      </button>
                    ) : (
                      <>
                        <Link
                          to={`/admin/drivers/${driver.id}`}
                          className="w-full flex items-center gap-2 px-4 py-2.5 text-sm text-slate-300 hover:bg-slate-700 transition-colors"
                        >
                          <ChevronRight size={16} />
                          Voir le profil
                        </Link>
                        <button
                          onClick={(e) => { e.stopPropagation(); handleResetPassword(driver.id, `${driver.first_name || ''} ${driver.last_name || ''}`.trim() || driver.email); setOpenMenuId(null); }}
                          className="w-full flex items-center gap-2 px-4 py-2.5 text-sm text-blue-400 hover:bg-slate-700 transition-colors"
                          data-testid={`reset-password-${driver.id}`}
                        >
                          <KeyRound size={16} />
                          Reinitialiser mot de passe
                        </button>
                        <button 
                          onClick={(e) => { e.stopPropagation(); setStatusModal({ driver, action: 'suspend' }); setOpenMenuId(null); }}
                          className="w-full flex items-center gap-2 px-4 py-2.5 text-sm text-amber-400 hover:bg-slate-700 transition-colors"
                          data-testid={`suspend-${driver.id}`}
                        >
                          <PauseCircle size={16} />
                          Suspendre
                        </button>
                        <button 
                          onClick={(e) => { e.stopPropagation(); setStatusModal({ driver, action: 'terminate' }); setOpenMenuId(null); }}
                          className="w-full flex items-center gap-2 px-4 py-2.5 text-sm text-red-400 hover:bg-slate-700 transition-colors"
                          data-testid={`terminate-${driver.id}`}
                        >
                          <UserMinus size={16} />
                          Retirer
                        </button>
                      </>
                    )}
                  </div>
                )}
              </div>
            )}
            
            {!isArchived && !isSuspended && (
              <Link to={`/admin/drivers/${driver.id}`}>
                <ChevronRight size={16} className="text-slate-600 hover:text-amber-500 transition-colors" />
              </Link>
            )}
          </div>
        </div>

        {/* Status reason if suspended */}
        {isSuspended && driver.status_reason && (
          <div className="mb-3 bg-amber-500/10 rounded-lg px-3 py-2 text-sm">
            <span className="text-amber-400 font-medium">{driver.status_reason}</span>
            {driver.status_changed_at && (
              <span className="text-slate-500 ml-2">
                depuis le {new Date(driver.status_changed_at).toLocaleDateString('fr-CA')}
              </span>
            )}
          </div>
        )}

        {/* Archived reason */}
        {isArchived && (
          <div className="mb-3 bg-red-500/10 rounded-lg px-3 py-2 text-sm">
            <span className="text-red-400 font-medium">{driver.status_reason || 'Motif non spécifié'}</span>
            {driver.status_reason_detail && (
              <p className="text-slate-400 mt-1">{driver.status_reason_detail}</p>
            )}
            {driver.terminated_at && (
              <span className="text-slate-500 block mt-1">
                Retiré le {new Date(driver.terminated_at).toLocaleDateString('fr-CA')}
              </span>
            )}
          </div>
        )}

        {/* Driver info */}
        {!isArchived && (
          <div className="space-y-2 text-sm">
            {driver.phone && (
              <div className="flex items-center gap-2 text-slate-400">
                <Phone size={14} />
                <span>{driver.phone}</span>
              </div>
            )}
            
            {!isSuspended && driver.location && (
              <div className="flex items-center gap-2 text-slate-400">
                <MapPin size={14} />
                <span>
                  {driver.location.latitude?.toFixed(4)}, {driver.location.longitude?.toFixed(4)}
                </span>
              </div>
            )}
            
            {!isSuspended && driver.location?.timestamp && (
              <div className="flex items-center gap-2 text-slate-500">
                <Clock size={14} />
                <span>
                  Dernière position: {new Date(driver.location.timestamp).toLocaleString('fr-CA')}
                </span>
              </div>
            )}
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="p-4 md:p-6 space-y-6">
      {/* Back Button */}
      <Link 
        to="/admin/dashboard"
        className="inline-flex items-center gap-2 text-slate-400 hover:text-amber-500 transition-colors"
      >
        <ArrowLeft size={18} />
        <span>Retour au tableau de bord</span>
      </Link>

      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-white flex items-center gap-2">
            <Users className="text-amber-500" />
            Chauffeurs
          </h1>
          <p className="text-slate-400">
            {activeDrivers.length} actif(s) · {suspendedDrivers.length} suspendu(s) · {onlineCount} en ligne
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button
            onClick={() => setShowInviteModal(true)}
            className="bg-amber-500 hover:bg-amber-600 text-black"
            data-testid="invite-driver-btn"
          >
            <UserPlus size={18} className="mr-2" />
            Inviter un chauffeur
          </Button>
          <button
            onClick={() => { fetchDrivers(); fetchArchivedDrivers(); }}
            className="flex items-center gap-2 bg-slate-800 hover:bg-slate-700 text-white px-4 py-2 rounded-lg transition-colors"
          >
            <RefreshCw size={18} className={loading ? 'animate-spin' : ''} />
            Actualiser
          </button>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-1 bg-slate-900 rounded-lg p-1 border border-slate-800">
        <button
          onClick={() => setActiveTab('active')}
          className={`flex-1 flex items-center justify-center gap-2 px-4 py-2.5 rounded-md text-sm font-medium transition-colors ${
            activeTab === 'active' 
              ? 'bg-amber-500 text-black' 
              : 'text-slate-400 hover:text-white hover:bg-slate-800'
          }`}
          data-testid="tab-active"
        >
          <Users size={16} />
          Actifs ({activeDrivers.length + suspendedDrivers.length})
        </button>
        <button
          onClick={() => setActiveTab('archived')}
          className={`flex-1 flex items-center justify-center gap-2 px-4 py-2.5 rounded-md text-sm font-medium transition-colors ${
            activeTab === 'archived' 
              ? 'bg-red-500 text-white' 
              : 'text-slate-400 hover:text-white hover:bg-slate-800'
          }`}
          data-testid="tab-archived"
        >
          <Archive size={16} />
          Retirés ({archivedDrivers.length})
        </button>
      </div>

      {/* Pending Invitations */}
      {activeTab === 'active' && connectedDrivers.pending?.length > 0 && (
        <div className="bg-blue-500/10 border border-blue-500/30 rounded-xl p-4">
          <h3 className="text-blue-400 font-semibold mb-2 flex items-center gap-2">
            <Clock size={18} />
            Invitations en attente ({connectedDrivers.pending.length})
          </h3>
          <div className="space-y-2">
            {connectedDrivers.pending.map(item => (
              <div key={item.connection_id} className="flex items-center justify-between bg-slate-900/50 rounded-lg p-3">
                <div>
                  <p className="text-white">{item.driver.first_name} {item.driver.last_name}</p>
                  <p className="text-slate-400 text-sm">{item.driver.email}</p>
                </div>
                <span className="text-blue-400 text-sm">En attente de réponse</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* --- PENDING-INVITATIONS-UI-V1 --- */}
      {/* Invitations envoyees (email pas encore accepte) */}
      {activeTab === 'active' && pendingInvitations.length > 0 && (
        <div className="bg-amber-500/10 border border-amber-500/30 rounded-xl p-4" data-testid="pending-invitations-section">
          <h3 className="text-amber-400 font-semibold mb-3 flex items-center gap-2">
            <Mail size={18} />
            Invitations envoyées ({pendingInvitations.length})
          </h3>
          <p className="text-slate-400 text-xs mb-3">
            Ces invitations n&apos;ont pas encore été acceptées. Renvoyez l&apos;email si le chauffeur ne l&apos;a pas reçu, ou créez la connexion manuellement si son compte existe déjà.
          </p>
          <div className="space-y-2">
            {pendingInvitations.map(inv => {
              const isExpired = inv.expires_at && new Date(inv.expires_at) < new Date();
              return (
                <div
                  key={inv.id}
                  className="flex flex-col sm:flex-row sm:items-center sm:justify-between bg-slate-900/50 rounded-lg p-3 gap-2"
                  data-testid={`invitation-row-${inv.id}`}
                >
                  <div className="flex-1 min-w-0">
                    <p className="text-white font-medium truncate">{inv.driver_email}</p>
                    <div className="flex flex-wrap items-center gap-2 text-xs text-slate-400 mt-1">
                      <span>Invité par {inv.admin_name || 'Admin'}</span>
                      {inv.created_at && (
                        <span>· {new Date(inv.created_at).toLocaleDateString('fr-CA')}</span>
                      )}
                      {isExpired ? (
                        <span className="text-red-400 font-medium">· Expirée</span>
                      ) : (
                        <span className="text-amber-400">· En attente</span>
                      )}
                    </div>
                  </div>
                  <div className="flex gap-2 flex-shrink-0">
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => handleResendInvitation(inv.id, inv.driver_email)}
                      data-testid={`resend-invitation-${inv.id}`}
                      className="border-amber-500/50 text-amber-400 hover:bg-amber-500/10"
                    >
                      <Send size={14} className="mr-1" />
                      Renvoyer
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => handleForceCreateConnection(inv.id, inv.driver_email)}
                      data-testid={`force-create-connection-${inv.id}`}
                      className="border-blue-500/50 text-blue-400 hover:bg-blue-500/10"
                    >
                      <UserCheck size={14} className="mr-1" />
                      Créer connexion
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => handleDeleteInvitation(inv.id, inv.driver_email)}
                      data-testid={`delete-invitation-${inv.id}`}
                      className="border-red-500/50 text-red-400 hover:bg-red-500/10"
                    >
                      <Trash2 size={14} className="mr-1" />
                      Supprimer
                    </Button>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Search */}
      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" size={20} />
        <input
          type="text"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          placeholder="Rechercher un chauffeur..."
          className="w-full bg-slate-900 border border-slate-800 rounded-lg py-3 pl-10 pr-4 text-white focus:outline-none focus:border-amber-500"
          data-testid="search-drivers"
        />
      </div>

      {/* Active Tab Content */}
      {activeTab === 'active' && (
        <>
          {/* Suspended Drivers */}
          {filteredSuspended.length > 0 && (
            <div>
              <h3 className="text-amber-400 font-semibold mb-3 flex items-center gap-2">
                <PauseCircle size={18} />
                Chauffeurs suspendus ({filteredSuspended.length})
              </h3>
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4 mb-6">
                {filteredSuspended.map(driver => (
                  <DriverCard key={driver.id} driver={driver} />
                ))}
              </div>
            </div>
          )}

          {/* Active Drivers */}
          {loading ? (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
              {[...Array(6)].map((_, i) => (
                <div key={i} className="bg-slate-900 rounded-xl h-40 animate-pulse" />
              ))}
            </div>
          ) : filteredActive.length === 0 && filteredSuspended.length === 0 ? (
            <div className="text-center py-12">
              <Users className="text-slate-600 mx-auto mb-4" size={48} />
              <p className="text-slate-400">Aucun chauffeur trouvé</p>
              <p className="text-slate-500 text-sm mt-1">
                Les chauffeurs doivent partager leurs données avec votre organisation
              </p>
            </div>
          ) : (
            <div>
              {filteredSuspended.length > 0 && (
                <h3 className="text-green-400 font-semibold mb-3 flex items-center gap-2">
                  <CheckCircle size={18} />
                  Chauffeurs actifs ({filteredActive.length})
                </h3>
              )}
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
                {filteredActive.map(driver => (
                  <DriverCard key={driver.id} driver={driver} />
                ))}
              </div>
            </div>
          )}
        </>
      )}

      {/* Archived Tab Content */}
      {activeTab === 'archived' && (
        <>
          {filteredArchived.length === 0 ? (
            <div className="text-center py-12">
              <Archive className="text-slate-600 mx-auto mb-4" size={48} />
              <p className="text-slate-400">Aucun chauffeur retiré</p>
              <p className="text-slate-500 text-sm mt-1">
                Les chauffeurs retirés apparaîtront ici avec le motif
              </p>
            </div>
          ) : (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
              {filteredArchived.map(driver => (
                <DriverCard key={driver.id} driver={driver} isArchived />
              ))}
            </div>
          )}
        </>
      )}

      {/* Invite Modal */}
      {showInviteModal && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4">
          <div className="bg-slate-900 rounded-xl w-full max-w-md border border-slate-700">
            <div className="flex items-center justify-between p-4 border-b border-slate-700">
              <h2 className="text-lg font-semibold text-white flex items-center gap-2">
                <UserPlus className="text-amber-500" />
                Inviter un chauffeur
              </h2>
              <button 
                onClick={() => setShowInviteModal(false)}
                className="text-slate-400 hover:text-white"
              >
                <X size={20} />
              </button>
            </div>
            
            <form onSubmit={handleInvite} className="p-4 space-y-4">
              <div>
                <label className="block text-sm text-slate-400 mb-2">
                  Adresse email du chauffeur
                </label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" size={18} />
                  <Input
                    type="email"
                    value={inviteEmail}
                    onChange={(e) => setInviteEmail(e.target.value)}
                    placeholder="chauffeur@exemple.com"
                    className="pl-10 bg-slate-800 border-slate-700 text-white"
                    required
                    data-testid="invite-email-input"
                  />
                </div>
              </div>
              
              <div className="bg-slate-800/50 rounded-lg p-3">
                <p className="text-sm text-slate-400">
                  Le chauffeur recevra un email d'invitation. S'il n'a pas de compte, 
                  il pourra en créer un gratuitement. Il devra accepter votre invitation 
                  pour partager ses données avec vous.
                </p>
              </div>
              
              <div className="flex gap-3">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setShowInviteModal(false)}
                  className="flex-1 border-slate-700"
                >
                  Annuler
                </Button>
                <Button
                  type="submit"
                  disabled={inviteLoading || !inviteEmail}
                  className="flex-1 bg-amber-500 hover:bg-amber-600 text-black"
                  data-testid="send-invite-btn"
                >
                  {inviteLoading ? (
                    <RefreshCw size={18} className="animate-spin" />
                  ) : (
                    <>
                      <Send size={18} className="mr-2" />
                      Envoyer
                    </>
                  )}
                </Button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Status Action Modal (Suspend / Terminate) */}
      {statusModal && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4">
          <div className="bg-slate-900 rounded-xl w-full max-w-md border border-slate-700">
            <div className="flex items-center justify-between p-4 border-b border-slate-700">
              <h2 className="text-lg font-semibold text-white flex items-center gap-2">
                {statusModal.action === 'suspend' ? (
                  <>
                    <PauseCircle className="text-amber-500" />
                    Suspendre le chauffeur
                  </>
                ) : (
                  <>
                    <UserMinus className="text-red-500" />
                    Retirer le chauffeur
                  </>
                )}
              </h2>
              <button 
                onClick={() => { setStatusModal(null); setStatusReason(''); setStatusDetail(''); }}
                className="text-slate-400 hover:text-white"
              >
                <X size={20} />
              </button>
            </div>
            
            <div className="p-4 space-y-4">
              {/* Driver info */}
              <div className="flex items-center gap-3 bg-slate-800/50 rounded-lg p-3">
                <div className="w-10 h-10 bg-slate-700 rounded-full flex items-center justify-center text-amber-500 font-bold">
                  {statusModal.driver.first_name?.[0]}{statusModal.driver.last_name?.[0]}
                </div>
                <div>
                  <p className="text-white font-medium">{statusModal.driver.first_name} {statusModal.driver.last_name}</p>
                  <p className="text-slate-400 text-sm">{statusModal.driver.email}</p>
                </div>
              </div>

              {/* Warning */}
              <div className={`rounded-lg p-3 flex items-start gap-2 ${
                statusModal.action === 'suspend' 
                  ? 'bg-amber-500/10 border border-amber-500/30' 
                  : 'bg-red-500/10 border border-red-500/30'
              }`}>
                <AlertTriangle size={18} className={statusModal.action === 'suspend' ? 'text-amber-400 mt-0.5' : 'text-red-400 mt-0.5'} />
                <p className={`text-sm ${statusModal.action === 'suspend' ? 'text-amber-300' : 'text-red-300'}`}>
                  {statusModal.action === 'suspend' 
                    ? "Le chauffeur restera dans votre liste mais sera masqué de la carte, du dispatch et du portail client. Vous pourrez le réactiver en un clic."
                    : "Le chauffeur sera retiré de votre liste active et déplacé dans les archives. Vous pourrez le réinviter ultérieurement."
                  }
                </p>
              </div>

              {/* Reason select */}
              <div>
                <label className="block text-sm text-slate-400 mb-2">Motif *</label>
                <select
                  value={statusReason}
                  onChange={(e) => setStatusReason(e.target.value)}
                  className="w-full bg-slate-800 border border-slate-700 rounded-lg py-2.5 px-3 text-white focus:outline-none focus:border-amber-500"
                  required
                  data-testid="status-reason-select"
                >
                  <option value="">Sélectionner un motif...</option>
                  {(statusModal.action === 'suspend' ? SUSPEND_REASONS : TERMINATE_REASONS).map(r => (
                    <option key={r} value={r}>{r}</option>
                  ))}
                </select>
              </div>

              {/* Detail textarea */}
              <div>
                <label className="block text-sm text-slate-400 mb-2">Détails (optionnel)</label>
                <textarea
                  value={statusDetail}
                  onChange={(e) => setStatusDetail(e.target.value)}
                  placeholder="Informations supplémentaires..."
                  className="w-full bg-slate-800 border border-slate-700 rounded-lg py-2.5 px-3 text-white focus:outline-none focus:border-amber-500 h-20 resize-none"
                  data-testid="status-detail-input"
                />
              </div>

              {/* Buttons */}
              <div className="flex gap-3">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => { setStatusModal(null); setStatusReason(''); setStatusDetail(''); }}
                  className="flex-1 border-slate-700"
                >
                  Annuler
                </Button>
                <Button
                  onClick={() => handleStatusChange(
                    statusModal.driver.id,
                    statusModal.action === 'suspend' ? 'suspended' : 'terminated',
                    statusReason,
                    statusDetail
                  )}
                  disabled={statusLoading || !statusReason}
                  className={`flex-1 ${
                    statusModal.action === 'suspend' 
                      ? 'bg-amber-500 hover:bg-amber-600 text-black' 
                      : 'bg-red-500 hover:bg-red-600 text-white'
                  }`}
                  data-testid="confirm-status-btn"
                >
                  {statusLoading ? (
                    <RefreshCw size={18} className="animate-spin" />
                  ) : statusModal.action === 'suspend' ? (
                    'Suspendre'
                  ) : (
                    'Retirer'
                  )}
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}

      {resetPasswordResult && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4">
          <div className="bg-slate-900 rounded-xl w-full max-w-lg border border-blue-600/60" data-testid="reset-password-modal">
            <div className="flex items-center justify-between p-4 border-b border-slate-700">
              <h2 className="text-lg font-semibold text-white flex items-center gap-2">
                <KeyRound className="text-blue-400" size={20} />
                Mot de passe reinitialise
              </h2>
              <button onClick={() => setResetPasswordResult(null)} className="text-slate-400 hover:text-white"><X size={20} /></button>
            </div>
            <div className="p-4 space-y-4">
              <div className="bg-blue-950/40 border border-blue-800/50 rounded-lg p-3">
                <p className="text-sm text-blue-200">Nouveau mot de passe temporaire pour <strong>{resetPasswordResult.name}</strong>. Transmettez-le par SMS.</p>
              </div>
              <div>
                <label className="block text-sm text-slate-400 mb-2">Email</label>
                <div className="bg-slate-800 border border-slate-700 rounded-lg p-3 text-sm text-white font-mono break-all">{resetPasswordResult.email}</div>
              </div>
              <div>
                <label className="block text-sm text-slate-400 mb-2">Mot de passe temporaire</label>
                <div className="bg-slate-800 border border-blue-700 rounded-lg p-3 text-lg text-white font-mono tracking-widest text-center cursor-pointer select-all" onClick={handleCopyResetPassword} data-testid="reset-password-value">{resetPasswordResult.password}</div>
                <p className="text-xs text-slate-500 mt-1">8 caracteres sans symboles</p>
              </div>
              <div className="flex flex-col sm:flex-row gap-2">
                <Button type="button" onClick={handleCopyResetPassword} className="flex-1 bg-blue-500 hover:bg-blue-600 text-white">Copier</Button>
                <Button type="button" onClick={handleShareResetSMS} variant="outline" className="flex-1 border-slate-700 text-white">Envoyer par SMS</Button>
              </div>
              <Button type="button" variant="ghost" onClick={() => setResetPasswordResult(null)} className="w-full text-slate-400 hover:text-white">Fermer</Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default DriversPage;
